import { useRef, useState } from "react";
import { toast } from "sonner";
import { uploadDocument } from "../../services/documentService";
import styles from "./QuestionUpload.module.css";

interface QuestionUploadProps {
	documentIds: string[];
	onUploadComplete: (documentId: string) => void;
	onRemoveDocument: (documentId: string) => void;
	onUploadSuccess?: () => void;
	t: (key: string) => string;
}

export function QuestionUpload({
	documentIds,
	onUploadComplete,
	onRemoveDocument,
	onUploadSuccess,
	t,
}: QuestionUploadProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);

	async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		if (file.size > 10 * 1024 * 1024) {
			toast.error(t("fileTooLarge"));
			return;
		}

		const formData = new FormData();
		formData.append("file", file);
		formData.append("category", "medical_record");

		setIsUploading(true);
		try {
			const result = await uploadDocument(formData);
			onUploadComplete(result.document.id);
			onUploadSuccess?.();
		} catch {
			toast.error(t("uploadError"));
		} finally {
			setIsUploading(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	}

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				accept=".png,.jpg,.jpeg,.pdf"
				onChange={handleFileSelect}
				className={styles.hiddenInput}
			/>
			<button
				type="button"
				className={styles.uploadArea}
				onClick={() => fileInputRef.current?.click()}
				disabled={isUploading}
			>
				{isUploading ? (
					<span className={styles.uploadPrompt}>
						{t("questions.medicalDocuments.uploadPrompt")}...
					</span>
				) : (
					<>
						<svg
							width="32"
							height="32"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							className={styles.cameraIcon}
							role="img"
							aria-label="Camera"
						>
							<path
								d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
								stroke="#E45BC2"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z"
								stroke="#E45BC2"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span className={styles.uploadPrompt}>
							{t("questions.medicalDocuments.uploadPrompt")}
						</span>
						<span className={styles.uploadHint}>{t("uploadHint")}</span>
					</>
				)}
			</button>
			{documentIds.length > 0 && (
				<div>
					{documentIds.map((docId) => (
						<div key={docId} className={styles.uploadedFile}>
							<span className={styles.fileName}>{docId}</span>
							<button
								type="button"
								className={styles.removeButton}
								onClick={() => onRemoveDocument(docId)}
							>
								x
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
